import { AdminPage, AdminPageHeader } from '../components/layout/AdminPage';
/**
 * @page DashboardPage
 * @description 系統儀表板 — 今日概覽 + KPI + 快速入口
 */
import { BrandIcon } from '../components/layout/BrandIcon';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconCards, IconChevronRight } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.ts';
import { useAuthStore } from '../store/auth.store.ts';
import { useFeature } from '../hooks/useFeature.tsx';
import { useDialogFocus } from '../hooks/useDialogFocus';
import { formatAmount } from './tournament/tournamentDisplay';
import type { TournamentDto } from '@pokerroom/types';

// ============================================================
// KPI types
// ============================================================
interface KPIData {
  range: string;
  startDate: string;
  endDate: string;
  revenue: { total: string; change: number };
  sessions: { total: number; change: number };
  activeMembers: { total: number; change: number };
  avgSpendPerPlayer: { total: string; change: number };
  trend: Array<{ date: string; revenue: string; sessions: number; activeMembers: number }>;
}

type KPIRange = '7d' | '30d' | '90d';
const KPI_RANGES: KPIRange[] = ['7d', '30d', '90d'];
const SECTION_TITLE = 'text-base font-semibold text-base-content';

// ============================================================
// API types
// ============================================================
interface DailySession {
  id: string;
  status: string;
  openingCash: number;
  date: string;
}

// ============================================================
// Stat Card
// ============================================================
function StatCard({
  label,
  value,
  sub,
  icon,
  color,
  to,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: string;
  color: string;
  to?: string;
}) {
  const inner = (
    <div
      className={`h-full bg-base-200 p-5 border border-base-300 transition-colors ${to ? 'hover:border-primary' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 ${color}`}>
          <BrandIcon value={icon} size={24} />
        </div>
      </div>
      <div className="text-2xl font-bold text-base-content tabular-nums">{value}</div>
      <div className="text-sm text-base-content mt-1">{label}</div>
      {sub && <div className="text-sm text-base-content/80 mt-1 tabular-nums">{sub}</div>}
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

// ============================================================
// Quick Link Card
// ============================================================
function QuickLink({
  to,
  icon,
  label,
  desc,
}: {
  to: string;
  icon: string;
  label: string;
  desc?: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 min-h-14 bg-base-200 border border-base-300 hover:border-primary transition-colors group"
    >
      <BrandIcon value={icon} size={24} />
      <div className="min-w-0">
        <div className="font-medium text-base-content text-sm">{label}</div>
        {desc && <div className="hidden sm:block text-sm text-base-content/80">{desc}</div>}
      </div>
      <IconChevronRight
        size={18}
        aria-hidden="true"
        className="ml-auto shrink-0 text-base-content/80 group-hover:text-primary transition-colors"
      />
    </Link>
  );
}

// ============================================================
// KPI Card with sparkline
// ============================================================
export function KPICard({
  label,
  value,
  change,
  changeLabel,
  data,
}: {
  label: string;
  value: string | number;
  change: number;
  changeLabel: string;
  data: number[];
}) {
  const max = Math.max(...data, 1);
  const points = data
    .map((v, i) => `${(i / Math.max(data.length - 1, 1)) * 100},${100 - (v / max) * 80}`)
    .join(' ');

  return (
    <div className="bg-base-200 p-4 border border-base-300">
      <h3 className="text-sm text-base-content mb-1">{label}</h3>
      <p className="text-xl font-bold text-base-content tabular-nums">{value}</p>
      {change !== 0 && (
        <p className="text-sm text-base-content/80 mb-2 tabular-nums">
          {`${changeLabel} ${change > 0 ? '+' : ''}${change}%`}
        </p>
      )}
      {data.length > 1 && (
        <svg
          viewBox="0 0 100 100"
          className="w-full h-8 text-primary"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline
            points={points}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

// ============================================================
// KPI Section
// ============================================================
function KPISection() {
  const { i18n } = useTranslation('common');
  const allowedActions = useAuthStore((s) => s.allowedActions);
  const hasAccounting = useFeature('accounting.enabled');
  const canViewKPI = hasAccounting && (allowedActions?.includes('dashboard:view_kpi') ?? false);
  const [range, setRange] = useState<KPIRange>('7d');

  const { data: kpi } = useQuery<KPIData>({
    queryKey: ['dashboard-kpi', range],
    queryFn: () => api.get(`/accounting/reports/kpi?range=${range}`),
    retry: false,
    staleTime: 60_000,
    enabled: canViewKPI,
  });

  if (!canViewKPI || !kpi) return null;

  return <KPIOverview kpi={kpi} range={range} onRange={setRange} locale={i18n.language} />;
}

export function KPIOverview({
  kpi,
  range,
  onRange,
  locale,
}: {
  kpi: KPIData;
  range: KPIRange;
  onRange: (range: KPIRange) => void;
  locale: string;
}) {
  const { t } = useTranslation('common');
  const rangeLabels: Record<KPIRange, string> = {
    '7d': t('kpi7d'),
    '30d': t('kpi30d'),
    '90d': t('kpi90d'),
  };
  const changeLabel = t('kpiChangeVs', { range: rangeLabels[range] });

  return (
    <section className="mb-8" aria-labelledby="dashboard-kpi-title">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 id="dashboard-kpi-title" className={SECTION_TITLE}>
          {t('kpiTitle')}
        </h2>
        <div role="group" aria-label={t('kpiRange')} className="flex">
          {KPI_RANGES.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={range === r}
              onClick={() => onRange(r)}
              className={`min-h-11 px-3 text-sm border border-base-300 -ml-px first:ml-0 transition-colors ${
                range === r
                  ? 'bg-base-300 text-base-content font-semibold border-base-content'
                  : 'text-base-content hover:bg-base-300'
              }`}
            >
              {rangeLabels[r]}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard
          label={t('kpiRevenue', { defaultValue: '營收' })}
          value={formatAmount(kpi.revenue.total, locale)}
          change={kpi.revenue.change}
          changeLabel={changeLabel}
          data={kpi.trend.map((d) => Number(d.revenue))}
        />
        <KPICard
          label={t('kpiSessions', { defaultValue: '場次' })}
          value={kpi.sessions.total.toLocaleString(locale)}
          change={kpi.sessions.change}
          changeLabel={changeLabel}
          data={kpi.trend.map((d) => d.sessions)}
        />
        <KPICard
          label={t('kpiActiveMembers', { defaultValue: '活躍會員' })}
          value={kpi.activeMembers.total.toLocaleString(locale)}
          change={kpi.activeMembers.change}
          changeLabel={changeLabel}
          data={kpi.trend.map((d) => d.activeMembers)}
        />
        <KPICard
          label={t('kpiAvgSpend', { defaultValue: '平均客單價' })}
          value={formatAmount(kpi.avgSpendPerPlayer.total, locale)}
          change={kpi.avgSpendPerPlayer.change}
          changeLabel={changeLabel}
          data={kpi.trend.map((d) => (d.sessions > 0 ? Number(d.revenue) / d.sessions : 0))}
        />
      </div>
    </section>
  );
}

// ============================================================
// Dashboard
// ============================================================
export default function DashboardPage() {
  const { t, i18n } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const hasMember = useFeature('member.enabled');
  const hasAccounting = useFeature('accounting.enabled');

  // 進行中的賽事
  const { data: activeTournamentsData } = useQuery<{ data: TournamentDto[]; total: number }>({
    queryKey: ['dashboard-tournaments'],
    queryFn: () => api.get('/tournament?status=IN_PROGRESS&limit=5'),
    retry: false,
  });
  const activeTournaments = activeTournamentsData?.data ?? [];

  // 今日帳務（需要 accounting.enabled）
  const { data: dailySession } = useQuery<DailySession | null>({
    queryKey: ['dashboard-daily-session'],
    queryFn: () => api.get<DailySession>('/accounting/sessions/active').catch(() => null),
    retry: false,
    enabled: hasAccounting,
  });

  // 會員統計（需要 member.enabled）
  const { data: memberData } = useQuery<{ data: unknown[]; total: number }>({
    queryKey: ['dashboard-members'],
    queryFn: () => api.get('/members?limit=1'),
    retry: false,
    enabled: hasMember,
  });

  const today = new Date().toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  // 暫停中的賽事
  const { data: pausedTournamentsData } = useQuery<{ data: TournamentDto[]; total: number }>({
    queryKey: ['dashboard-paused-tournaments'],
    queryFn: () => api.get('/tournament?status=PAUSED&limit=50'),
    retry: false,
  });
  const pausedCount = pausedTournamentsData?.total ?? 0;

  const [showEmergencyConfirm, setShowEmergencyConfirm] = useState<'pause' | 'resume' | null>(null);
  const [emergencyError, setEmergencyError] = useState(false);
  const openEmergency = (kind: 'pause' | 'resume') => {
    setEmergencyError(false);
    setShowEmergencyConfirm(kind);
  };

  const emergencyPauseMutation = useMutation({
    mutationFn: () => api.post('/tournament/emergency-pause', {}),
    onSuccess: () => {
      setShowEmergencyConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['dashboard-tournaments'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-paused-tournaments'] });
    },
    onError: (err) => {
      setEmergencyError(true);
      console.warn('[Dashboard] emergencyPause failed:', err instanceof Error ? err.message : err);
    },
  });

  const emergencyResumeMutation = useMutation({
    mutationFn: () => api.post('/tournament/emergency-resume', {}),
    onSuccess: () => {
      setShowEmergencyConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['dashboard-tournaments'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-paused-tournaments'] });
    },
    onError: (err) => {
      setEmergencyError(true);
      console.warn('[Dashboard] emergencyResume failed:', err instanceof Error ? err.message : err);
    },
  });

  const activeCount = activeTournaments.length;
  const sessionOpen = dailySession?.status === 'OPEN';

  const emergencyPending = emergencyPauseMutation.isPending || emergencyResumeMutation.isPending;

  return (
    <AdminPage className="admin-page-body">
      <AdminPageHeader
        title={t('welcome', { name: user?.displayName ?? user?.username })}
        description={today}
      />

      <section className="mb-6" aria-labelledby="dashboard-quick-title">
        <h2 id="dashboard-quick-title" className={`${SECTION_TITLE} mb-3`}>
          {t('quickActions')}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <QuickLink
            to="/tournament/create"
            icon="➕"
            label={t('createTournament')}
            desc={t('createTournamentDesc')}
          />
          <QuickLink to="/tournament" icon="🏆" label={t('nav.tournamentList')} />
          <QuickLink to="/display" icon="📺" label={t('tvDisplay')} />
          {hasAccounting && (
            <QuickLink
              to="/accounting/expenses"
              icon="📝"
              label={t('expenseEntry')}
              desc={t('expenseEntryDesc')}
            />
          )}
        </div>
      </section>

      <section className="mb-8" aria-labelledby="dashboard-active-title">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 id="dashboard-active-title" className={SECTION_TITLE}>
            {t('activeTournamentsSection')}
          </h2>
          <div className="flex gap-2">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={() => openEmergency('pause')}
                className={DANGER_OUTLINE}
              >
                {t('emergencyPause')}
              </button>
            )}
            {pausedCount > 0 && (
              <button type="button" onClick={() => openEmergency('resume')} className={OUTLINE}>
                {t('emergencyResume')}
              </button>
            )}
          </div>
        </div>
        {activeCount === 0 ? (
          <div className="bg-base-200 border border-base-300 p-8 text-center">
            <IconCards
              size={32}
              stroke={1.5}
              aria-hidden="true"
              className="mx-auto mb-2 text-base-content/80"
            />
            <p className="text-base-content/80 text-sm">{t('noActiveTournaments')}</p>
            <Link
              to="/tournament/create"
              className="mt-2 inline-flex min-h-11 items-center px-2 text-primary text-sm hover:underline"
            >
              {t('createFirstTournament')}
            </Link>
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {activeTournaments.map((tournament) => (
                <li
                  key={tournament.id}
                  className="flex items-center gap-3 px-3 py-2 sm:p-4 bg-base-200 border border-base-300 hover:border-primary transition-colors"
                >
                  <Link
                    to={`/tournament/${tournament.id}`}
                    className="flex-1 min-w-0 min-h-11 flex flex-col justify-center"
                  >
                    <span className="font-medium text-base-content text-sm truncate">
                      {tournament.name}
                    </span>
                    <span className="text-sm text-base-content/80 tabular-nums">
                      {t('registrationCount', { count: tournament._count?.entries ?? 0 })}
                    </span>
                  </Link>
                  <Link to={`/tournament/${tournament.id}/timer`} className={`${OUTLINE} shrink-0`}>
                    {t('timerBtn')}
                    <span className="sr-only">{tournament.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/tournament"
              className="mt-2 flex min-h-11 items-center justify-center text-primary text-sm hover:underline"
            >
              {t('viewAllTournaments')}
            </Link>
          </>
        )}
      </section>

      <section className="relative mb-8" aria-labelledby="dashboard-today-title">
        <h2 id="dashboard-today-title" className="sr-only">
          {t('todayOverview')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            label={t('activeTournaments')}
            value={activeCount}
            icon="🏆"
            color="bg-primary/10 text-primary"
            to="/tournament"
          />
          {hasMember && (
            <StatCard
              label={t('totalMembers')}
              value={memberData ? memberData.total.toLocaleString(i18n.language) : '—'}
              icon="👥"
              color="bg-info/10 text-info"
              to="/members"
            />
          )}
          {hasAccounting && (
            <StatCard
              label={t('todayAccounting')}
              value={sessionOpen ? t('sessionOpen') : t('sessionClosed')}
              sub={
                sessionOpen
                  ? t('openingCash', {
                      amount: dailySession?.openingCash?.toLocaleString(i18n.language) ?? 0,
                    })
                  : undefined
              }
              icon="💰"
              color={sessionOpen ? 'bg-success/10 text-success' : 'bg-base-300 text-base-content'}
              to="/accounting/open"
            />
          )}
        </div>
      </section>

      <KPISection />

      {showEmergencyConfirm && (
        <EmergencyConfirmDialog
          kind={showEmergencyConfirm}
          count={showEmergencyConfirm === 'pause' ? activeCount : pausedCount}
          pending={emergencyPending}
          failed={emergencyError}
          onCancel={() => setShowEmergencyConfirm(null)}
          onConfirm={() => {
            setEmergencyError(false);
            if (showEmergencyConfirm === 'pause') emergencyPauseMutation.mutate();
            else emergencyResumeMutation.mutate();
          }}
        />
      )}
    </AdminPage>
  );
}

/** Confirms the venue-wide pause or resume; Escape and 取消 close it without a request. */
export function EmergencyConfirmDialog({
  kind,
  count,
  pending,
  failed,
  onCancel,
  onConfirm,
}: {
  kind: 'pause' | 'resume';
  count: number;
  pending: boolean;
  failed: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('common');
  const dialogRef = useDialogFocus(true);
  const pause = kind === 'pause';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="emergency-title"
        aria-describedby="emergency-desc"
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
        className="bg-base-200 p-6 w-full max-w-sm border border-base-300"
      >
        <h2 id="emergency-title" className="text-lg font-bold text-base-content mb-2">
          {pause ? t('confirmEmergencyPause') : t('confirmEmergencyResume')}
        </h2>
        <p id="emergency-desc" className="text-sm text-base-content mb-4">
          {pause ? t('emergencyPauseDesc', { count }) : t('emergencyResumeDesc', { count })}
        </p>
        {failed && (
          <p role="alert" className="mb-4 border border-error p-3 text-sm text-error">
            {t('emergencyFailed')}
          </p>
        )}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className={OUTLINE}>
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={pause ? DANGER : PRIMARY}
          >
            {pending ? t('processing') : t('confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

const ACTION =
  'relative inline-flex min-h-11 items-center justify-center gap-1 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const DANGER = `${ACTION} bg-error text-error-content hover:bg-error/90`;
const OUTLINE = `${ACTION} border border-base-300 text-base-content hover:bg-base-300`;
const DANGER_OUTLINE = `${ACTION} border border-error text-error hover:bg-error/10`;
